import { ICotizacionValues } from '../../../Providers/CotizacionStore';
import { idDeProvincia } from './catalogoProvincias';
import { resolverIds } from './catalogoVehiculos';
import { advertenciasDe, publicarAvisosEmbudo } from './avisosEmbudo';
import * as bff from './endpoints';
import {
  guardarCotizacionId,
  guardarSesionCotizacion,
  leerSesionCotizacion,
} from './quoteSession';
import {
  BffContactoRequest,
  BffContactoResponse,
  BffPreguntasContacto,
  BffSeleccionResponse,
  Ramo,
  TipoUsoVehiculo,
} from './types';

/**
 * Armado del funnel contra el BFF: contacto (§3.2) y selección de plan (§3.4).
 * La conversión del store al DTO nuevo es pura y está testeada.
 */

/** "dd/MM/yyyy" o {dia,mes,año} -> "yyyy-MM-dd" (ISO, como pide el BFF). */
export const aFechaIso = (fecha?: {
  dia?: string;
  mes?: string;
  año?: string;
}): string => {
  if (!fecha?.dia || !fecha?.mes || !fecha?.año) return '';
  return `${fecha.año}-${fecha.mes.padStart(2, '0')}-${fecha.dia.padStart(
    2,
    '0'
  )}`;
};

/** "Masculino"/"M" -> "M"; "Femenino"/"F" -> "F"; cualquier otra cosa -> "X". */
export const aSexo = (genero?: string): 'F' | 'M' | 'X' => {
  const valor = (genero || '').trim().toUpperCase();
  if (valor.startsWith('M')) return 'M';
  if (valor.startsWith('F')) return 'F';
  return 'X';
};

/** Año del select; "0 km" se traduce a año actual + esCeroKm. */
export const interpretarAnio = (
  anio?: string
): { anio: number; esCeroKm: boolean } => {
  const texto = (anio || '').toLowerCase();
  if (!texto) return { anio: 0, esCeroKm: false };
  if (texto.includes('0 km')) {
    return { anio: new Date().getFullYear(), esCeroKm: true };
  }
  const parseado = parseInt(texto, 10);
  return {
    anio: Number.isNaN(parseado) ? 0 : parseado,
    esCeroKm: false,
  };
};

const TIPOS_USO: TipoUsoVehiculo[] = ['PARTICULAR', 'COMERCIAL', 'TAXI_REMIS'];

/**
 * Uso del vehículo para el contacto.
 *
 * El cotizador NO pregunta el uso: el select "Tipo de uso" recién aparece en
 * la revisión de datos de la emisión, después de elegir el plan. Por eso el
 * default es `PARTICULAR`, que es lo mismo que hacía el monolito (hardcodeaba
 * `particular` al cotizar). Si algún día el paso de datos lo pregunta, alcanza
 * con guardarlo en el store y llega solo.
 */
export const aTipoUso = (valor?: string): TipoUsoVehiculo => {
  const texto = (valor || '').trim().toUpperCase().replace(/[\s-]+/g, '_');
  if (TIPOS_USO.includes(texto as TipoUsoVehiculo)) {
    return texto as TipoUsoVehiculo;
  }
  // Códigos del select viejo de revisión de datos: 1 = particular, 2 = comercial.
  if (texto === '2' || texto === 'COMERCIAL') return 'COMERCIAL';
  if (texto === 'TAXI' || texto === 'REMIS' || texto === 'TAXI/REMIS') {
    return 'TAXI_REMIS';
  }
  return 'PARTICULAR';
};

/**
 * Bloque `preguntas` del contacto. Sólo viajan las que están contestadas: una
 * pregunta sin respuesta se omite, porque para el BFF `false` y "no contestó"
 * son cosas distintas. Devuelve `undefined` si no hay ninguna.
 */
export const armarPreguntas = (
  preguntas?: BffPreguntasContacto
): BffPreguntasContacto | undefined => {
  if (!preguntas) return undefined;
  const salida: BffPreguntasContacto = {};
  (['vtv', 'creditoCbu', 'disponeVehiculo', 'prendado'] as const).forEach(
    (clave) => {
      if (typeof preguntas[clave] === 'boolean') salida[clave] = preguntas[clave];
    }
  );
  return Object.keys(salida).length ? salida : undefined;
};

/**
 * `cotizacionValues` -> body de `POST /{ramo}/v1/contacto`.
 *
 * El teléfono va sin ceros a la izquierda (misma normalización que el BFF) y
 * el email en minúsculas. La localidad viaja como texto y el código postal es
 * obligatorio: Segucore recibe CP + provincia y no resuelve localidad -> CP.
 */
export const construirContactoRequest = (
  valores: ICotizacionValues,
  extra: {
    esMobile: boolean;
    /** Respuestas del funnel, si alguna pantalla las llega a capturar. */
    preguntas?: BffPreguntasContacto;
  }
): BffContactoRequest => {
  const { anio, esCeroKm } = interpretarAnio(valores.año?.value);
  const nombres = {
    marca: valores.marca?.value,
    modelo: valores.modelo?.value,
    version: valores.version?.value,
  };
  const ids = resolverIds(nombres);

  return {
    origen: valores.parametrosIniciales?.origen || '',
    tipoCotizador: valores.parametrosIniciales?.tipoCotizador || 'WEB',
    landingUrl: valores.landingUrl || '',
    refererUrl: valores.referer || '',
    esMobile: extra.esMobile,
    vehiculo: {
      marcaId: ids.marcaId,
      modeloId: ids.modeloId,
      versionId: ids.versionId,
      marcaNombre: (nombres.marca || '').trim(),
      modeloNombre: (nombres.modelo || '').trim(),
      versionNombre: (nombres.version || '').trim(),
      anio,
      esCeroKm,
      patente: valores.patente || undefined,
      tieneGnc: false,
      sumaAsegurada: null,
      tipoUso: aTipoUso(valores.tipoUso?.value),
      // El `versionId` del catálogo del BFF ES el CODIA (verificado 19/09/2026).
      codigoInfoAuto: ids.versionId ? String(ids.versionId) : undefined,
    },
    persona: {
      nombre: (valores.nombre || '').trim(),
      apellido: (valores.apellido || '').trim(),
      email: (valores.email || '').trim().toLowerCase(),
      codigoArea: (valores.telefono?.prefijo || '').replace(/^0+/, ''),
      telefono: (valores.telefono?.numero || '').replace(/\D/g, ''),
      fechaNacimiento: aFechaIso(valores.fechaNac),
      sexo: aSexo(valores.genero?.value),
      provinciaId: idDeProvincia(valores.provincia?.value),
      localidad: (valores.localidad?.value || '').trim(),
      codigoPostal: (valores.codigoPostal || '').trim(),
      cuandoContrata: valores.cuandoContrata?.label || '',
    },
    preguntas: armarPreguntas(extra.preguntas),
  };
};

/**
 * Crea el lead y guarda el JWT de cotización en el store. El recaptcha se
 * mintea fresco acá adentro (acción `contacto`): NO se reusa el token que el
 * paso de datos guardaba en el store en el camino viejo.
 */
export const crearContacto = async (
  ramo: Ramo,
  valores: ICotizacionValues
): Promise<BffContactoResponse> => {
  const body = construirContactoRequest(valores, {
    esMobile: typeof window !== 'undefined' && window.innerWidth < 768,
  });
  const respuesta = await bff.postContacto(ramo, body);
  // Embudo EOL (US 23704): avisos de datos_iniciales que no frenan.
  publicarAvisosEmbudo(advertenciasDe(respuesta));
  guardarSesionCotizacion({
    token: respuesta.token,
    expiraEn: respuesta.expiraEn,
    prospectoId: respuesta.prospectoId,
  });
  return respuesta;
};

/**
 * Elige el plan. La respuesta trae `emisionOnline.habilitada`, que es lo que
 * decide si se sigue a emisión online o se deriva a WhatsApp.
 */
export const seleccionarPlan = async (
  ramo: Ramo,
  itemId: string
): Promise<BffSeleccionResponse> => {
  const { cotizacionId } = leerSesionCotizacion();
  if (!cotizacionId) {
    throw new Error('No hay cotización activa para seleccionar un plan.');
  }
  const respuesta = await bff.postSeleccion(ramo, cotizacionId, itemId);
  // Embudo EOL (US 23704): avisos de cotizacion_elegida que no frenan.
  publicarAvisosEmbudo(advertenciasDe(respuesta));
  guardarCotizacionId(respuesta.cotizacionId);
  return respuesta;
};
